import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import { useHeadlineQueriesCRUD } from "@/widget-components/newsquawk-headlines/api/headline-query/useHeadlineQueriesCRUD";
import { usePortfoliosCRUD } from "@/pages/PortfolioBuilder/api/usePortfoliosCRUD";
import useAuth from "@/hooks/useAuth";
import { useTagsWatchlistsCRUD } from "@/shared/api/pref-storage/tags-watchlists/useTagsWatchlistsCRUD";
import { groupPortfoliosByType } from "@/widget-components/firehose-widgets/shared/hooks/searchUtils";
import {
  validateKeywordsSize,
  validateTermsTotal,
  MAX_KEYWORDS_COUNT,
  MAX_KEYWORDS_TOTAL_CHARS,
} from "@/pages/PortfolioBuilder/keywordLimits";
import type { Portfolio } from "@/types/Portfolio";
import type { CategoryQuery } from "@/widget-components/newsquawk-headlines/models/CategoryQuery";
import { emptyNotifications, type FilterNotifications } from "@/types/notifications";
import toast from "react-hot-toast";
import { useConfirm } from "@/ui-components/ConfirmDialog";
import * as Sentry from "@sentry/react";
import { derivedViewColor, nextViewColor } from "@/shared/utils/savedViewHighlight";
import {
  clearPrebuiltCombinedColor,
  getPrebuiltCombinedColorsSnapshot,
  setPrebuiltCombinedColor,
  subscribeToPrebuiltCombinedColors,
} from "@/pages/PortfolioBuilder/prebuiltCombinedOverrides";

import { toCategories, type Category } from "./tagCategories";
import { useTypedTerms } from "./useTypedTerms";
import {
  assignTypedSlots,
  isIncludedTagWatchlist,
  isLinkedElsewhere,
  storageFor,
  typedTermGroups,
  typedTermsWatchlists,
  type ListStorage,
} from "@/pages/PortfolioBuilder/listStorage";
import {
  canInclude,
  contentOf,
  partsOf,
  recogniseIncluded,
  withIncluded,
  type CombinedParts,
} from "@/pages/PortfolioBuilder/includedLists";
import { ruleShape, switchesHeldBy, type Box } from "@/pages/PortfolioBuilder/builderModel";
import { buildTickerIndex, isKnownTicker } from "@/pages/PortfolioBuilder/tickerSuggestions";
import { formatListName } from "@/shared/utils/formatListName";
import useTabStore from "@/stores/useTabStore";
import {
  clearPrebuiltOverride,
  getPrebuiltOverridesSnapshot,
  setPrebuiltOverride,
  subscribeToPrebuiltOverrides,
} from "@/pages/PortfolioBuilder/prebuiltWatchlists";
import { useDemoGuideStore } from "@/demo-guide/useDemoGuideStore";
import { trackFeatureUsage } from "@/utils/gtm";

/** Same members, order aside. */
const sameMembers = (a: string[], b: string[]) =>
  a.length === b.length && a.every((x) => b.includes(x));

const uniqueById = <T extends { id: number }>(items: T[]): T[] => [
  ...new Map(items.map((item) => [item.id, item])).values(),
];

/**
 * Pre-fill for CREATE mode (concierge "describe it" flow) — seeds the form
 * without entering edit mode; saving still creates a new filter.
 *
 * A concierge filter is a UNION across axes (tags OR watchlists), so the
 * watchlist ids matter as much as the tags — carrying only `tags` silently
 * halves the filter's reach.
 */
export interface FilterDraft {
  name?: string;
  searchKeyword?: string;
  tags?: string[];
  /** Ticker/keyword watchlists this filter matches against. */
  portfolioIds?: number[];
  /** Tag watchlists this filter matches against. */
  tagsWatchlistIds?: number[];
  filterMode?: "AND" | "OR";
  keywordOp?: "AND" | "OR";
  /** Typed keyword vs keyword-watchlist terms. Needed for an exact copy of a
   *  library filter; everything else leaves it at the default. */
  keywordWatchlistOp?: "AND" | "OR";
  /** Name of the Newsquawk combination this was opened from in the library —
   *  the builder then says so rather than "New". */
  basedOn?: string;
  /** That combination itself, so its colour can be kept for this user
   *  without copying it. See libraryChanges. */
  libraryId?: number;
  /** The colour the user currently sees it highlight in. */
  color?: string;
  /**
   * Tickers and keywords to start the boxes with — the concierge's single
   * watchlist, a create link, a list typed elsewhere.
   */
  tickers?: string[];
  keywords?: string[];
  /**
   * What the Newsquawk list being copied is. A watchlist's colour is kept as
   * the user's override in one store, a combination's in another.
   */
  libraryKind?: "watchlist" | "combined";
}

/** Which kind of record the builder is editing, if any. */
type EditingKind = ListStorage | "tag";

/**
 * A new list, reported as whatever it saved as. The onboarding rail's "Build
 * a watchlist" step and the checklist's create_watchlist task both complete
 * on this event, so every path that makes a list has to send it — edits and
 * conversions don't.
 */
function trackListCreated(detail: ListStorage | "demo") {
  trackFeatureUsage({
    feature_name: "watchlist",
    feature_action: "create",
    feature_detail: detail,
  });
}

export interface UseFilterBuilderOptions {
  queryId?: number | null;
  /**
   * A plain watchlist to edit here. It stays a watchlist while it is only
   * typed terms matched as "any"; given a tag, a list, a keyword expression
   * or an AND, it becomes the hidden half of a combined watchlist — see
   * listStorage.ts.
   */
  portfolioId?: number | null;
  /**
   * A tag watchlist to edit. Customers no longer build these — tags are part
   * of any list — so saving one makes it a combined watchlist in its place.
   * One the desk is editing, or one another list includes, is saved in place
   * (see inPlaceReason).
   */
  tagWatchlistId?: number | null;
  draft?: FilterDraft | null;
  /**
   * Lists to build against instead of the signed-in user's own.
   *
   * Only the guided demo passes this. It opens this editor on a recorded plan
   * for a logged-out visitor, whose usePortfoliosCRUD is empty — so the lists
   * the draft selects would have no rows to sit in and the left pane would
   * come up blank next to a query referring to them. Same override PlanPreview
   * takes, and for the same reason.
   */
  portfoliosOverride?: Portfolio[];
  /**
   * `id` is absent only for the guided demo, which writes nothing. `kind` is
   * which record the list ended up as; absent means combined.
   */
  onSaved?: (
    queryName: string,
    categories: string[],
    id?: number,
    kind?: EditingKind
  ) => void;
  onDeleted?: () => void;
}

export function useFilterBuilder(options: UseFilterBuilderOptions) {
  const confirm = useConfirm();
  const { authenticated } = useAuth();
  const { queriesQuery, createMutation, editMutation, deleteMutation } = useHeadlineQueriesCRUD();
  const {
    portfolios: ownPortfolios,
    createMutation: portfolioCreate,
    updateMutation: portfolioUpdate,
    deleteMutation: portfolioDelete,
  } = usePortfoliosCRUD();
  // The hook still runs unconditionally; the override just wins. Logged out it
  // resolves to an empty list anyway, so this costs nothing in the demo case.
  const portfolios = options.portfoliosOverride ?? ownPortfolios;
  const {
    watchlistsQuery: tagsWatchlistsQuery,
    updateMutation: tagWatchlistUpdate,
    deleteMutation: tagWatchlistDelete,
  } = useTagsWatchlistsCRUD();
  const markWatchlistCreated = useDemoGuideStore((s) => s.markWatchlistCreated);
  const tagsWatchlists = tagsWatchlistsQuery.data || [];
  const headlineQueries = queriesQuery.data;
  /**
   * Whether the combined lists have arrived, or never will (logged out). A
   * draft waits for them: without them the lists it includes can't be told
   * apart from its own picks.
   */
  const queriesSettled = !authenticated || queriesQuery.isFetched;
  /** Read by the seeding effects without re-running them on a refetch. */
  const queriesRef = useRef(headlineQueries);
  queriesRef.current = headlineQueries;

  // ── Form state ────────────────────────────────────────────────────────
  const [queryName, setQueryName] = useState("");
  const [searchKeyword, setSearchKeyword] = useState("");
  const [selectedPortfolioIds, setSelectedPortfolioIds] = useState<number[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<Category[]>([]);
  const [selectedTagsWatchlistIds, setSelectedTagsWatchlistIds] = useState<number[]>([]);
  /**
   * Combined watchlists ticked into this one — see includedLists.ts. Kept as
   * themselves while building, and merged in on save.
   */
  const [includedIds, setIncludedIds] = useState<number[]>([]);
  const [filterMode, setFilterMode] = useState<"AND" | "OR">("OR");
  const [keywordOp, setKeywordOp] = useState<"AND" | "OR">("OR");
  /**
   * Not offered: the builder's Keywords box is "any of these". Kept as loaded
   * so an edit or copy of an older list doesn't change what it matches.
   */
  const [keywordWatchlistOp, setKeywordWatchlistOp] = useState<"AND" | "OR">("OR");
  const [notifications, setNotifications] = useState<FilterNotifications>(emptyNotifications());
  /**
   * Highlight colour the user picked. Null means "not picked" — see
   * displayColor for what stands in, and what gets saved, until they do.
   */
  const [color, setColor] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  /**
   * What each tag is (company, topic…), as far as it's known, for the
   * swamping warning. Filled as tags are searched for and named.
   */
  const [tagKinds, setTagKinds] = useState<Record<string, string>>({});

  /** The Keywords and Tickers boxes' own entries. */
  const terms = useTypedTerms();
  /**
   * The hidden watchlists a combined list being edited keeps its typed terms
   * in, with what each held — see typedTermsWatchlists. Their ids come out
   * of the linked lists, since the boxes are where those terms are edited.
   */
  const [typedSlots, setTypedSlots] = useState<Pick<Portfolio, "id" | "tickers" | "keywords">[]>([]);
  /**
   * Which record has been loaded into the form. The load effects depend on
   * the queries and lists, which refetch — on window focus among other
   * times — and reloading then threw away an edit in progress.
   */
  const loadedKey = useRef<string | null>(null);
  const seededDraft = useRef<FilterDraft | null>(null);

  /**
   * Desk fields, for when this builder is the whole form: the full-screen
   * builder, which is also where a desk user's pencil on a Newsquawk
   * combination opens. Inert for everyone else — the UI is gated on
   * `useIsDeskUser()`.
   *
   * Seeded from the record being edited. The notes ride along on every save, so an edit that never loaded them sent
   * them back empty and cleared the desk's description and when-to-use.
   *
   * `publishToLibrary` is create-only: the server ignores owner_type on PATCH.
   */
  const [publishToLibrary, setPublishToLibrary] = useState(false);
  const [description, setDescription] = useState("");
  const [whenToUse, setWhenToUse] = useState("");
  const [assetClasses, setAssetClasses] = useState<string[]>([]);

  /** Which kind of record is being edited, if any. */
  const editingKind: EditingKind | undefined =
    options.queryId != null
      ? "combined"
      : options.portfolioId != null
        ? "watchlist"
        : options.tagWatchlistId != null
          ? "tag"
          : undefined;
  const isEditing = editingKind !== undefined;
  const editingPortfolio =
    options.portfolioId != null
      ? ownPortfolios.find((p) => p.id === options.portfolioId)
      : undefined;
  const editingTagWatchlist =
    options.tagWatchlistId != null
      ? tagsWatchlists.find((t) => t.id === options.tagWatchlistId)
      : undefined;
  const isNewsquawkOwned =
    editingKind === "combined"
      ? headlineQueries?.find((q) => q.id === options.queryId)?.owner_type ===
        "NEWSQUAWK"
      : editingKind === "tag"
        ? editingTagWatchlist?.owner_type === "NEWSQUAWK"
        : editingPortfolio?.owner_type === "NEWSQUAWK";

  const includedElsewhere =
    options.tagWatchlistId != null &&
    isIncludedTagWatchlist(headlineQueries, options.tagWatchlistId);

  /**
   * A list that saves in place and never converts: its own terms (or tags)
   * change; tags and other lists can't be added.
   *
   * - "library": the desk editing a Newsquawk watchlist or tag watchlist.
   *   Customers select library lists in their own dashboards by id. Turning
   *   one into a combined watchlist would leave every one of those selections
   *   pointing at a list they can no longer see — and the browser can only
   *   move the selections of whoever is signed in.
   * - "ingredient": a tag watchlist another list includes — see
   *   isIncludedTagWatchlist. In place, a change to its tags reaches every
   *   list that includes it.
   */
  const inPlaceReason: "library" | "ingredient" | undefined =
    (editingKind === "watchlist" || editingKind === "tag") && isNewsquawkOwned
      ? "library"
      : includedElsewhere
        ? "ingredient"
        : undefined;
  const inPlace = inPlaceReason !== undefined;

  // ── Lists ──────────────────────────────────────────────────────────────
  /**
   * Every watchlist the builder can name: the visible ones, and the hidden
   * ones combined lists embed — an index list's constituents, or a list the
   * user's own includes. A linked hidden list still shows, as a chip named
   * after the list it was made for.
   */
  const knownPortfolios = useMemo(
    () =>
      uniqueById([
        ...portfolios,
        ...(headlineQueries ?? []).flatMap((q) => q.portfolios ?? []),
      ]),
    [portfolios, headlineQueries]
  );

  /** Suggestions and the "is it a ticker?" check, from every list loaded. */
  const tickerIndex = useMemo(() => buildTickerIndex(knownPortfolios), [knownPortfolios]);
  const isKnown = useCallback(
    (symbol: string) => isKnownTicker(tickerIndex, symbol),
    [tickerIndex]
  );

  /**
   * Combined watchlists that can be ticked in: all but the one being edited
   * or copied, which can't include itself.
   */
  const combinedLists = useMemo(
    () =>
      (headlineQueries ?? []).filter(
        (q) =>
          q.id !== undefined &&
          q.id !== options.queryId &&
          q.id !== options.draft?.libraryId
      ),
    [headlineQueries, options.queryId, options.draft?.libraryId]
  );

  const includedLists = useMemo(
    () =>
      includedIds
        .map((id) => combinedLists.find((q) => q.id === id))
        .filter((q): q is CategoryQuery => !!q),
    [includedIds, combinedLists]
  );
  /** Included lists that merge exactly — see canInclude. */
  const mergeableIncluded = useMemo(() => includedLists.filter(canInclude), [includedLists]);
  /**
   * Included lists that use AND, which can only be started from. Saving waits
   * until each is started from or removed.
   */
  const blockedIncluded = useMemo(
    () => includedLists.filter((q) => !canInclude(q)),
    [includedLists]
  );

  // ── Portfolio grouping ────────────────────────────────────────────────
  const selectedPortfolios = useMemo(
    () =>
      selectedPortfolioIds
        .map((id) => knownPortfolios.find((p) => p.id === id))
        .filter((p): p is Portfolio => !!p),
    [knownPortfolios, selectedPortfolioIds]
  );
  const selectedGrouped = useMemo(
    () => groupPortfoliosByType(selectedPortfolios),
    [selectedPortfolios]
  );

  const hasBothWatchlistTypes =
    selectedGrouped.tickerOnly.length > 0 && selectedGrouped.keywordOnly.length > 0;

  // Only while a new combination is being put together. On an edit or a
  // library copy this fired as the stored lists loaded and silently turned a
  // saved OR into AND — a different list, saved without anyone asking.
  useEffect(() => {
    if (isEditing || options.draft) return;
    if (hasBothWatchlistTypes && keywordOp === "OR") {
      setKeywordOp("AND");
    }
  }, [hasBothWatchlistTypes]);

  // Resolve selected tag watchlist objects
  const selectedTagWatchlistObjects = useMemo(
    () => tagsWatchlists.filter((tw) => selectedTagsWatchlistIds.includes(tw.id)),
    [tagsWatchlists, selectedTagsWatchlistIds]
  );

  // ── The rule ──────────────────────────────────────────────────────────
  /** The builder's own picks, before included lists are merged in. */
  const ownParts: CombinedParts = useMemo(
    () => ({
      portfolioIds: selectedPortfolioIds,
      categories: selectedCategories.map((c) => String(c.id ?? "")).filter(Boolean),
      tagsWatchlistIds: selectedTagsWatchlistIds,
      searchKeyword: searchKeyword.trim(),
    }),
    [selectedPortfolioIds, selectedCategories, selectedTagsWatchlistIds, searchKeyword]
  );
  /** What saving writes: the picks with the included lists' parts copied in. */
  const mergedParts = useMemo(
    () => withIncluded(ownParts, mergeableIncluded),
    [ownParts, mergeableIncluded]
  );

  /** Which boxes hold anything, for the switches between them. */
  const filled: Record<Box, boolean> = useMemo(() => {
    const unitsIn = (box: Box) =>
      includedLists.some((q) => {
        const content = contentOf(q);
        if (box === "tags") return content.tags && !content.keywords && !content.tickers;
        return content[box];
      });
    return {
      keywords:
        terms.keywords.length > 0 ||
        searchKeyword.trim() !== "" ||
        selectedGrouped.keywordOnly.length > 0 ||
        selectedGrouped.mixed.length > 0 ||
        unitsIn("keywords"),
      tickers:
        terms.tickers.length > 0 ||
        selectedGrouped.tickerOnly.some((p) => p.tickers.length > 0) ||
        selectedGrouped.mixed.length > 0 ||
        unitsIn("tickers"),
      tags:
        selectedCategories.length > 0 ||
        selectedTagsWatchlistIds.length > 0 ||
        unitsIn("tags"),
    };
  }, [
    terms.keywords.length,
    terms.tickers.length,
    searchKeyword,
    selectedGrouped,
    selectedCategories.length,
    selectedTagsWatchlistIds.length,
    includedLists,
  ]);

  /**
   * The first picked list keeping each switch at "or", so it matches the way
   * it does on its own — see switchesHeldBy.
   */
  const heldBy = useMemo(() => {
    const held: { keywordOp?: string; filterMode?: string } = {};
    const picks = [
      ...selectedGrouped.mixed.map((p) => ({
        name: formatListName(p.name),
        content: { keywords: true, tickers: true, tags: false },
      })),
      ...mergeableIncluded.map((q) => ({
        name: formatListName(q.queryName),
        content: contentOf(q),
      })),
    ];
    for (const pick of picks) {
      const holds = switchesHeldBy(pick.content);
      if (holds.keywordOp) held.keywordOp ??= pick.name;
      if (holds.filterMode) held.filterMode ??= pick.name;
    }
    return held;
  }, [selectedGrouped.mixed, mergeableIncluded]);

  const shape = useMemo(
    () => ruleShape({ filled, keywordOp, filterMode, heldBy }),
    [filled, keywordOp, filterMode, heldBy]
  );

  // ── Keyword validation ────────────────────────────────────────────────
  const aggregatedKeywords = useMemo(() => {
    const listKeywords = mergedParts.portfolioIds
      .map((id) => knownPortfolios.find((p) => p.id === id))
      .flatMap((p) => p?.keywords ?? []);
    const expression = mergedParts.searchKeyword.trim();
    return [
      ...terms.keywords,
      ...listKeywords,
      ...(expression ? [expression] : []),
    ];
  }, [mergedParts, knownPortfolios, terms.keywords]);

  const keywordsSize = validateKeywordsSize(aggregatedKeywords);

  // ── Loading ───────────────────────────────────────────────────────────
  /**
   * Spreads a combined list's parts across the boxes: its own typed terms
   * into the Keywords and Tickers boxes, the combined lists it includes back
   * into one chip each, and the rest as the picks they are.
   */
  const spreadParts = useCallback(
    (parts: CombinedParts, exclude: (number | undefined)[]) => {
      const all = queriesRef.current ?? [];
      const candidates = all.filter((q) => !exclude.includes(q.id));
      const { includedIds: found, rest } = recogniseIncluded(parts, candidates, all);
      setIncludedIds(found);
      setSearchKeyword(rest.searchKeyword);
      setSelectedPortfolioIds(rest.portfolioIds);
      setSelectedTagsWatchlistIds(rest.tagsWatchlistIds);
      setSelectedCategories(toCategories(rest.categories));
    },
    []
  );

  // ── Load existing query for editing ───────────────────────────────────
  useEffect(() => {
    if (editingKind !== "combined" || !headlineQueries) return;
    const key = `q-${options.queryId}`;
    if (loadedKey.current === key) return;
    const q = headlineQueries.find((query) => query.id === options.queryId);
    if (!q) return;
    loadedKey.current = key;

    // Typed terms back into their boxes, out of the linked lists.
    const typed = typedTermsWatchlists(q, headlineQueries);
    setTypedSlots(typed.map(({ id, tickers, keywords }) => ({ id, tickers, keywords })));
    terms.load({
      tickers: [...new Set(typed.flatMap((p) => p.tickers))],
      keywords: [...new Set(typed.flatMap((p) => p.keywords ?? []))],
    });
    const typedIds = new Set(typed.map((p) => p.id));
    const parts = partsOf(q);
    spreadParts(
      { ...parts, portfolioIds: parts.portfolioIds.filter((id) => !typedIds.has(id)) },
      [q.id]
    );

    setQueryName(q.queryName);
    setFilterMode(q.filterMode ?? "OR");
    setKeywordOp(q.keywordOp ?? "OR");
    setKeywordWatchlistOp(q.keywordWatchlistOp ?? "OR");
    setColor(q.color ?? null);
    // Keep any extra backend sources from the API — PATCH replaces the whole map
    setNotifications(q.notifications ?? emptyNotifications());

    setPublishToLibrary(q.owner_type === "NEWSQUAWK");
    setDescription(q.description ?? "");
    setWhenToUse(q.whenToUse ?? "");
    setAssetClasses(q.assetClasses ?? []);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- terms.load is stable
  }, [editingKind, options.queryId, headlineQueries]);

  // ── Load a plain watchlist for editing ────────────────────────────────
  useEffect(() => {
    if (editingKind !== "watchlist" || !editingPortfolio) return;
    const key = `p-${editingPortfolio.id}`;
    if (loadedKey.current === key) return;
    loadedKey.current = key;

    const p = editingPortfolio;
    setQueryName(p.name);
    terms.load({ tickers: p.tickers, keywords: p.keywords });
    setTypedSlots([]);
    setSearchKeyword("");
    setSelectedPortfolioIds([]);
    setSelectedCategories([]);
    setSelectedTagsWatchlistIds([]);
    setIncludedIds([]);
    setColor(p.color ?? null);
    setNotifications(p.notifications ?? emptyNotifications());
    setPublishToLibrary(p.owner_type === "NEWSQUAWK");
    setDescription(p.description ?? "");
    setWhenToUse(p.when_to_use ?? "");
    setAssetClasses(p.categories ?? []);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- terms.load is stable
  }, [editingKind, editingPortfolio]);

  // ── Load a tag watchlist for editing ──────────────────────────────────
  useEffect(() => {
    if (editingKind !== "tag" || !editingTagWatchlist) return;
    const key = `t-${editingTagWatchlist.id}`;
    if (loadedKey.current === key) return;
    loadedKey.current = key;

    const t = editingTagWatchlist;
    setQueryName(t.name);
    terms.clear();
    setTypedSlots([]);
    setSearchKeyword("");
    setSelectedPortfolioIds([]);
    setSelectedTagsWatchlistIds([]);
    setIncludedIds([]);
    setSelectedCategories(toCategories(t.tags));
    setColor(null);
    setNotifications(t.notifications ?? emptyNotifications());
    setPublishToLibrary(t.owner_type === "NEWSQUAWK");
    setDescription(t.description ?? "");
    setWhenToUse(t.when_to_use ?? "");
    setAssetClasses(t.categories ?? []);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- terms.clear is stable
  }, [editingKind, editingTagWatchlist]);

  // ── Seed from a concierge draft (create mode only) ────────────────────
  // Once per draft, and only when the combined lists are in: the lists a
  // draft includes are recognised from them. A refetch doesn't re-seed.
  useEffect(() => {
    const draft = options.draft;
    if (!draft || isEditing || !queriesSettled) return;
    if (seededDraft.current === draft) return;
    seededDraft.current = draft;
    setQueryName(draft.name ?? "");
    if (draft.filterMode) setFilterMode(draft.filterMode);
    if (draft.keywordOp) setKeywordOp(draft.keywordOp);
    if (draft.keywordWatchlistOp) setKeywordWatchlistOp(draft.keywordWatchlistOp);
    // The other half of the union — without these a "tags [Fed] OR
    // watchlists [Fed Speakers]" filter arrives as tags only.
    spreadParts(
      {
        portfolioIds: draft.portfolioIds ?? [],
        categories: (draft.tags ?? []).map(String),
        tagsWatchlistIds: draft.tagsWatchlistIds ?? [],
        searchKeyword: draft.searchKeyword?.trim() ?? "",
      },
      [draft.libraryId]
    );
    setColor(draft.color ?? null);
    terms.load({ tickers: draft.tickers, keywords: draft.keywords });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- terms.load and spreadParts are stable
  }, [options.draft, isEditing, queriesSettled]);

  /**
   * What the picker shows until they pick.
   *
   * A new list gets the next palette colour, and is saved with it — it has no
   * id yet, so the id-derived colour it would otherwise fall back to cannot be
   * previewed. An existing list with no stored colour shows the derived one
   * it is already highlighting in, and keeps it unless changed.
   */
  const ownQueryCount =
    headlineQueries?.filter((q) => q.owner_type === "USER").length ?? 0;
  const displayColor =
    color ??
    (editingKind === "combined"
      ? derivedViewColor(options.queryId ?? undefined)
      : nextViewColor(ownQueryCount));
  const colorToSave = isEditing ? color : displayColor;

  // ── Toggle helpers ────────────────────────────────────────────────────
  const togglePortfolio = useCallback((id: number) => {
    setSelectedPortfolioIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  }, []);

  const toggleCategory = useCallback((cat: Category) => {
    setSelectedCategories((prev) => {
      const exists = prev.some((c) => c.id === cat.id);
      return exists ? prev.filter((c) => c.id !== cat.id) : [...prev, cat];
    });
  }, []);

  const toggleTagWatchlist = useCallback((id: number) => {
    setSelectedTagsWatchlistIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  }, []);

  /** Remembers what tags are, as searches and name lookups find out. */
  const rememberTagKinds = useCallback((kinds: Record<string, string>) => {
    setTagKinds((prev) => {
      const fresh = Object.entries(kinds).filter(([id, kind]) => prev[id] !== kind);
      return fresh.length === 0 ? prev : { ...prev, ...Object.fromEntries(fresh) };
    });
  }, []);

  /** The boxes hold something besides included combined lists. */
  const holdsOwnParts =
    terms.hasTerms ||
    terms.checks.length > 0 ||
    selectedPortfolioIds.length > 0 ||
    selectedCategories.length > 0 ||
    selectedTagsWatchlistIds.length > 0 ||
    searchKeyword.trim() !== "";
  const isEmpty = !holdsOwnParts && includedIds.length === 0;

  /**
   * A combined list that uses AND, as the starting point: its parts copied
   * into the boxes and its switches with them, the way "Build on" copies a
   * Newsquawk combination. Its watchlists are linked by id, so none of its
   * own hidden lists become this list's to edit.
   */
  const applyStartFrom = useCallback((query: CategoryQuery) => {
    const parts = partsOf(query);
    terms.clear();
    setIncludedIds([]);
    setSearchKeyword(parts.searchKeyword);
    setSelectedPortfolioIds(parts.portfolioIds);
    setSelectedTagsWatchlistIds(parts.tagsWatchlistIds);
    setSelectedCategories(toCategories(parts.categories));
    setFilterMode(query.filterMode ?? "OR");
    setKeywordOp(query.keywordOp ?? "OR");
    setKeywordWatchlistOp(query.keywordWatchlistOp ?? "OR");
    // eslint-disable-next-line react-hooks/exhaustive-deps -- terms.clear is stable
  }, []);

  /**
   * Start from an included list that uses AND. Anything else in the boxes is
   * replaced, so that is asked first.
   */
  const startFrom = useCallback(
    async (query: CategoryQuery) => {
      const replacing = holdsOwnParts || includedIds.some((id) => id !== query.id);
      if (replacing) {
        const ok = await confirm({
          title: `Start from “${formatListName(query.queryName)}”?`,
          message: "This replaces what's in the boxes with a copy of it.",
          confirmLabel: "Start from it",
        });
        if (!ok) return;
      }
      applyStartFrom(query);
    },
    [holdsOwnParts, includedIds, confirm, applyStartFrom]
  );

  /**
   * Ticks a combined watchlist in or out. One that uses AND can't be merged:
   * into an empty builder it becomes the starting point; otherwise it waits,
   * flagged, for "Start from it" or removal.
   */
  const toggleIncluded = useCallback(
    (id: number) => {
      if (includedIds.includes(id)) {
        setIncludedIds((prev) => prev.filter((x) => x !== id));
        return;
      }
      const query = combinedLists.find((q) => q.id === id);
      if (!query) return;
      if (!canInclude(query) && isEmpty) {
        applyStartFrom(query);
        return;
      }
      setIncludedIds((prev) => [...prev, id]);
    },
    [includedIds, combinedLists, isEmpty, applyStartFrom]
  );

  const clearAll = useCallback(() => {
    setSearchKeyword("");
    setSelectedPortfolioIds([]);
    setSelectedCategories([]);
    setSelectedTagsWatchlistIds([]);
    setIncludedIds([]);
    terms.clear();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- terms.clear is stable
  }, []);

  const reset = useCallback(() => {
    setQueryName("");
    setSearchKeyword("");
    setSelectedPortfolioIds([]);
    setSelectedCategories([]);
    setSelectedTagsWatchlistIds([]);
    setIncludedIds([]);
    setNotifications(emptyNotifications());
    setColor(null);
    setPublishToLibrary(false);
    setDescription("");
    setWhenToUse("");
    setAssetClasses([]);
    // Operators too: they carried over into the next new list.
    setFilterMode("OR");
    setKeywordOp("OR");
    setKeywordWatchlistOp("OR");
    terms.clear();
    setTypedSlots([]);
    loadedKey.current = null;
    seededDraft.current = null;
    setFormErrors({ categories: "", keywords: "" });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- terms.clear is stable
  }, []);

  // ── Submit ────────────────────────────────────────────────────────────
  const [formErrors, setFormErrors] = useState({ categories: "", keywords: "" });

  /**
   * What has changed on a Newsquawk list opened from the library as a copy,
   * split by what saving it has to do. Its terms, lists, tags, name or alerts
   * need a list of the user's own; its colour alone can be kept as their
   * override of the library one. Lists have no priority: which one wins
   * belongs to each widget (highlightOrder.ts).
   */
  const libraryChanges = useMemo(() => {
    const draft = options.draft;
    if (isEditing || !draft?.basedOn) return null;
    const content = !(
      // A copied watchlist's terms arrive in the boxes, so they are compared
      // with what was copied; for anything else that is nothing.
      sameMembers(terms.tickers, draft.tickers ?? []) &&
      sameMembers(terms.keywords, draft.keywords ?? []) &&
      terms.checks.length === 0 &&
      queryName.trim() === (draft.name ?? "").trim() &&
      // Parts compared merged, so an included list recognised in the copy
      // reads as the parts it came with.
      mergedParts.searchKeyword.trim() === (draft.searchKeyword ?? "").trim() &&
      sameMembers(mergedParts.categories, (draft.tags ?? []).map(String)) &&
      sameMembers(
        mergedParts.portfolioIds.map(String),
        (draft.portfolioIds ?? []).map(String)
      ) &&
      sameMembers(
        mergedParts.tagsWatchlistIds.map(String),
        (draft.tagsWatchlistIds ?? []).map(String)
      ) &&
      blockedIncluded.length === 0 &&
      // Only where the copy set them; otherwise they sit at their defaults.
      (draft.filterMode === undefined || filterMode === draft.filterMode) &&
      (draft.keywordOp === undefined || keywordOp === draft.keywordOp) &&
      (draft.keywordWatchlistOp === undefined ||
        keywordWatchlistOp === draft.keywordWatchlistOp) &&
      JSON.stringify(notifications) === JSON.stringify(emptyNotifications())
    );
    const look = color !== (draft.color ?? null);
    return { content, look };
  }, [
    options.draft,
    isEditing,
    queryName,
    mergedParts,
    blockedIncluded.length,
    filterMode,
    keywordOp,
    keywordWatchlistOp,
    color,
    notifications,
    terms.tickers,
    terms.keywords,
    terms.checks.length,
  ]);

  const libraryId = libraryChanges ? options.draft?.libraryId : undefined;
  const copyUnchanged =
    !!libraryChanges && !libraryChanges.content && !libraryChanges.look;
  const libraryKind = options.draft?.libraryKind ?? "combined";
  /** Only the look differs, on a library list it can be kept for. */
  const lookOnly =
    !!libraryChanges &&
    !libraryChanges.content &&
    libraryChanges.look &&
    libraryId != null;

  const combinedColors = useSyncExternalStore(
    subscribeToPrebuiltCombinedColors,
    getPrebuiltCombinedColorsSnapshot,
    getPrebuiltCombinedColorsSnapshot
  );
  const watchlistOverrides = useSyncExternalStore(
    subscribeToPrebuiltOverrides,
    getPrebuiltOverridesSnapshot,
    getPrebuiltOverridesSnapshot
  );
  const hasLibraryOverride =
    libraryId != null &&
    (libraryKind === "watchlist"
      ? watchlistOverrides[libraryId]?.color != null
      : combinedColors[libraryId] != null);

  /** Back to the look the desk set; closes the builder like a save. */
  const resetLibraryLook = useCallback(() => {
    if (libraryId == null) return;
    if (libraryKind === "watchlist") {
      // The colour only: an old priority stored beside it is left for the
      // one-time highlight-order conversion to read (useSeedHighlightOrders).
      clearPrebuiltOverride(libraryId, "color");
      toast.success(`“${options.draft?.basedOn}” is back to Newsquawk's colour`);
      options.onSaved?.(queryName, [], libraryId, "watchlist");
      return;
    }
    clearPrebuiltCombinedColor(libraryId);
    toast.success(`“${options.draft?.basedOn}” is back to Newsquawk's colour`);
    options.onSaved?.(queryName, [], libraryId);
  }, [libraryId, libraryKind, options, queryName]);

  // What the list would save as right now: which controls apply, and where
  // handleSubmit sends it. In place, it stays what it is.
  const parts = {
    hasTypedTerms: terms.hasTerms,
    pickedTags: mergedParts.categories.length,
    linkedLists: mergedParts.portfolioIds.length,
    tagWatchlists: mergedParts.tagsWatchlistIds.length,
    hasKeywordExpression: mergedParts.searchKeyword.trim() !== "",
    usesAnd: shape.usesAnd,
  };
  const storage: EditingKind =
    inPlace && editingKind ? editingKind : storageFor(parts, editingKind);

  /** Entries to settle before saving: held-back tickers and AND lists. */
  const toCheck = terms.checks.length + blockedIncluded.length;

  const moveListToCombined = useTabStore((state) => state.moveListToCombined);

  const handleSubmit = useCallback(async () => {
    if (copyUnchanged || toCheck > 0) return;
    // Only the look changed: keep it for this user; nothing is created.
    if (lookOnly && libraryId != null) {
      if (libraryKind === "watchlist") {
        setPrebuiltOverride(libraryId, { color: color ?? displayColor });
        toast.success(`Colour saved for “${options.draft?.basedOn}”`);
        options.onSaved?.(queryName, [], libraryId, "watchlist");
        return;
      }
      if (color) {
        setPrebuiltCombinedColor(libraryId, color);
        toast.success(`Colour saved for “${options.draft?.basedOn}”`);
        options.onSaved?.(queryName, [], libraryId);
        return;
      }
    }
    setFormErrors({ categories: "", keywords: "" });

    if (
      !parts.hasTypedTerms &&
      parts.pickedTags === 0 &&
      parts.linkedLists === 0 &&
      parts.tagWatchlists === 0 &&
      !parts.hasKeywordExpression
    ) {
      setFormErrors((prev) => ({
        ...prev,
        categories:
          "Add a keyword, ticker or tag, or tick a watchlist on the left.",
      }));
      return;
    }

    if (aggregatedKeywords.length > 0 && !keywordsSize.ok) {
      setFormErrors((prev) => ({
        ...prev,
        keywords: keywordsSize.error ?? "Keyword limit exceeded.",
      }));
      return;
    }
    const termsTotal = validateTermsTotal(terms.tickers, terms.keywords);
    if (!termsTotal.ok) {
      setFormErrors((prev) => ({
        ...prev,
        keywords: termsTotal.error ?? "Too many entries.",
      }));
      return;
    }

    const desk = {
      publishToLibrary,
      description,
      whenToUse,
      categories: assetClasses,
    };

    // Tags picked and tags an included list brings: to the combined watchlist
    // either way, since a watchlist holds none.
    const categories = mergedParts.categories;
    // A half-typed hex from the text field is not a colour.
    const validColor =
      colorToSave && /^#[0-9a-fA-F]{6}$/.test(colorToSave) ? colorToSave : undefined;

    if (!authenticated) {
      /**
       * Guided demo: nothing is written.
       *
       * Saving a list needs an account — /portfolios and /headline-queries
       * both refuse without one — so the button that ends the demo's most
       * important step failed for exactly the audience it was built for.
       * Nothing to create and nothing to clean up either: the demo watchlist
       * is already in the panels via demoWatchlist.ts, so "saving" reports
       * the truth about what an account would do.
       */
      toast.success("Watchlist created");
      // The guided demo's watchlist step ends here when the concierge's list
      // was opened in the builder; the store ignores a second call.
      markWatchlistCreated();
      trackListCreated("demo");
      options.onSaved?.(queryName, categories, undefined, storage);
      return;
    }

    setIsSaving(true);
    // Undone if what follows fails: hidden watchlists nothing links to, or a
    // watchlist half-converted and so hidden with no combined list to show it.
    const createdHiddenIds: number[] = [];
    let convertedId: number | undefined;
    try {
      // ── A tag watchlist saved in place — see inPlaceReason ─────────────
      if (storage === "tag" && options.tagWatchlistId != null) {
        const tags = selectedCategories.map((cat) => String(cat.id ?? ""));
        await tagWatchlistUpdate.mutateAsync({
          id: options.tagWatchlistId,
          data: {
            name: formatListName(queryName),
            tags,
            notifications,
            description: desk.description.trim() || null,
            when_to_use: desk.whenToUse.trim() || null,
            categories: desk.categories,
          },
        });
        toast.success("Watchlist updated");
        options.onSaved?.(queryName, tags, options.tagWatchlistId, "tag");
        return;
      }

      // ── Typed terms alone, matched as "any": a plain watchlist ──────────
      if (storage === "watchlist") {
        const fields = {
          name: formatListName(queryName),
          tickers: terms.tickers,
          keywords: terms.keywords,
          color: validColor ?? displayColor,
          notifications,
          description: desk.description.trim() || null,
          when_to_use: desk.whenToUse.trim() || null,
          categories: desk.categories,
        };
        let savedId: number;
        if (editingKind === "watchlist" && options.portfolioId != null) {
          // Only what this form edits — never a priority, which would make
          // the server renumber every list at or below it.
          await portfolioUpdate.mutateAsync({
            id: options.portfolioId,
            ...fields,
          } as Portfolio);
          // The desk's own local colour would mask the colour just saved to
          // the library list.
          if (isNewsquawkOwned) clearPrebuiltOverride(options.portfolioId, "color");
          toast.success("Watchlist updated");
          savedId = options.portfolioId;
        } else {
          const created = await portfolioCreate.mutateAsync({
            id: 0,
            ...fields,
            customizations: { includeRelatedHeadlines: false },
            owner_type: desk.publishToLibrary ? "NEWSQUAWK" : "USER",
          } as Portfolio);
          toast.success("Watchlist created");
          trackListCreated("watchlist");
          savedId = created.id;
        }
        options.onSaved?.(queryName, [], savedId, "watchlist");
        return;
      }

      // ── Anything more: a combined watchlist ─────────────────────────────
      // Its typed terms live in hidden watchlists inside it, tickers and
      // keywords apart — see typedTermGroups in listStorage.ts.
      const converting = editingKind === "watchlist" && options.portfolioId != null;
      const groups = typedTermGroups(terms.tickers, terms.keywords);
      const existing = converting
        ? [
            {
              id: options.portfolioId as number,
              tickers: editingPortfolio?.tickers ?? [],
              keywords: editingPortfolio?.keywords ?? [],
            },
          ]
        : typedSlots;
      const { slots, leftover } = assignTypedSlots(existing, groups);
      const typedIds: number[] = [];
      for (let i = 0; i < groups.length; i++) {
        const termFields = {
          name: formatListName(queryName),
          tickers: groups[i].tickers,
          keywords: groups[i].keywords,
        };
        const slot = slots[i];
        if (slot != null && converting && slot === options.portfolioId) {
          // Converted in place: it keeps its id, and its dashboard selections
          // follow it to the combined watchlist below.
          await portfolioUpdate.mutateAsync({
            id: slot,
            ...termFields,
            component_only: true,
          } as Portfolio);
          convertedId = slot;
          typedIds.push(slot);
        } else if (slot != null) {
          await portfolioUpdate.mutateAsync({ id: slot, ...termFields } as Portfolio);
          typedIds.push(slot);
        } else {
          const created = await portfolioCreate.mutateAsync({
            id: 0,
            ...termFields,
            component_only: true,
            color: validColor ?? displayColor,
            // The combined watchlist carries the alerts; this has no
            // subscription of its own to send them from.
            notifications: emptyNotifications(),
            customizations: { includeRelatedHeadlines: false },
            owner_type: desk.publishToLibrary ? "NEWSQUAWK" : "USER",
          } as Portfolio);
          createdHiddenIds.push(created.id);
          typedIds.push(created.id);
        }
      }

      const payload = {
        queryName,
        categories,
        searchKeyword: mergedParts.searchKeyword,
        portfolioIds: [
          ...typedIds,
          ...mergedParts.portfolioIds.filter((id) => !typedIds.includes(id)),
        ],
        tagsWatchlistIds: mergedParts.tagsWatchlistIds,
        // As drawn: a switch that isn't shown saves as "or" — see ruleShape.
        filterMode: shape.filterMode.value,
        keywordOp: shape.keywordOp.value,
        keywordWatchlistOp,
        notifications,
        // Omitted when not picked on an edit, so the stored value is left alone.
        color: validColor,
        description: desk.description.trim() || null,
        whenToUse: desk.whenToUse.trim() || null,
        assetClasses: desk.categories,
        // Create-only; the server ignores owner_type on PATCH.
        ...(desk.publishToLibrary ? ({ owner_type: "NEWSQUAWK" } as const) : {}),
      };

      let savedId: number;
      if (editingKind === "combined") {
        await editMutation.mutateAsync({ id: options.queryId as number, ...payload });
        savedId = options.queryId as number;
        // Hidden lists no longer needed go too, and their links with them —
        // unless another list includes them, which still needs them. A
        // Newsquawk list's are only unlinked: customers' copies ("Build on")
        // link them by id, and the desk can't see those copies to check.
        for (const id of leftover) {
          if (isNewsquawkOwned) continue;
          if (isLinkedElsewhere(headlineQueries, id, options.queryId ?? undefined)) continue;
          await portfolioDelete
            .mutateAsync(String(id))
            .catch((error) => Sentry.captureException(error));
        }
        toast.success("Watchlist updated");
      } else {
        const response = await createMutation.mutateAsync({ id: undefined, ...payload });
        savedId = Number(response.data?.id);
        createdHiddenIds.length = 0;
        convertedId = undefined;
        if (converting) {
          // Every widget that filtered or highlighted by the watchlist now
          // does so by the combined list it has become.
          moveListToCombined("watchlist", options.portfolioId as number, savedId);
          toast.success("Watchlist updated");
        } else if (editingKind === "tag" && options.tagWatchlistId != null) {
          // A tag watchlist is replaced by the combined watchlist holding its
          // tags: its dashboard selections move across, and it goes.
          moveListToCombined("tag", options.tagWatchlistId, savedId);
          await tagWatchlistDelete
            .mutateAsync(options.tagWatchlistId)
            .catch((error) => Sentry.captureException(error));
          toast.success("Watchlist updated");
        } else {
          toast.success("Watchlist created");
          trackListCreated("combined");
        }
      }
      options.onSaved?.(payload.queryName, payload.categories, savedId, "combined");
    } catch (error) {
      Sentry.captureException(error);
      // Best effort: nobody could see either to put it right by hand.
      for (const id of createdHiddenIds) {
        portfolioDelete.mutateAsync(String(id)).catch(() => {});
      }
      if (convertedId != null) {
        portfolioUpdate
          .mutateAsync({ id: convertedId, component_only: false } as Portfolio)
          .catch(() => {});
      }
      toast.error(isEditing ? "Failed to update watchlist" : "Failed to save watchlist");
    } finally {
      setIsSaving(false);
    }
  }, [
    queryName, selectedCategories, mergedParts, shape, keywordWatchlistOp,
    notifications, colorToSave, displayColor, editingKind, isEditing,
    options.queryId, options.portfolioId, aggregatedKeywords, keywordsSize,
    editMutation, createMutation, portfolioCreate, portfolioUpdate,
    portfolioDelete, options.onSaved, toCheck,
    authenticated, copyUnchanged, lookOnly, libraryId, color, publishToLibrary,
    description, whenToUse, assetClasses, terms.tickers, terms.keywords,
    typedSlots, editingPortfolio, headlineQueries, moveListToCombined,
    options.draft?.basedOn, options.tagWatchlistId, storage, parts, libraryKind,
    markWatchlistCreated, tagWatchlistUpdate, tagWatchlistDelete,
    isNewsquawkOwned,
  ]);

  const handleDelete = useCallback(async () => {
    const id =
      editingKind === "combined"
        ? options.queryId
        : editingKind === "tag"
          ? options.tagWatchlistId
          : options.portfolioId;
    if (id == null) return;
    const ok = await confirm({
      title: "Delete watchlist",
      message: "This watchlist will be permanently deleted. This action cannot be undone.",
      confirmLabel: "Delete",
      variant: "danger",
    });
    if (!ok) return;

    setIsDeleting(true);
    try {
      if (editingKind === "combined") {
        await deleteMutation.mutateAsync(id);
        // pref-storage deletes the hidden watchlists with it (PR #31); this
        // covers a server from before that, and 404s harmlessly after. One
        // another list includes is left for that list, and a Newsquawk
        // list's are left to the server: customers' copies link them.
        for (const slot of isNewsquawkOwned ? [] : typedSlots) {
          if (isLinkedElsewhere(headlineQueries, slot.id, id)) continue;
          portfolioDelete.mutateAsync(String(slot.id)).catch(() => {});
        }
      } else if (editingKind === "tag") {
        await tagWatchlistDelete.mutateAsync(id);
      } else {
        await portfolioDelete.mutateAsync(String(id));
      }
      toast.success("Watchlist deleted");
      options.onDeleted?.();
    } catch (error) {
      Sentry.captureException(error);
      toast.error("Failed to delete watchlist");
    } finally {
      setIsDeleting(false);
    }
  }, [
    editingKind,
    options.queryId,
    options.portfolioId,
    options.tagWatchlistId,
    deleteMutation,
    portfolioDelete,
    tagWatchlistDelete,
    typedSlots,
    headlineQueries,
    isNewsquawkOwned,
    options.onDeleted,
    confirm,
  ]);

  return {
    // Form state
    queryName, setQueryName,
    /** The Keywords and Tickers boxes' own entries, and those held back. */
    terms,
    searchKeyword, setSearchKeyword,
    selectedPortfolioIds, setSelectedPortfolioIds,
    selectedCategories, setSelectedCategories,
    selectedTagsWatchlistIds, setSelectedTagsWatchlistIds,
    includedIds,
    filterMode, setFilterMode,
    keywordOp, setKeywordOp,
    keywordWatchlistOp, setKeywordWatchlistOp,
    notifications, setNotifications,
    color, setColor, displayColor,
    tagKinds, rememberTagKinds,

    // Desk-only (see useIsDeskUser)
    publishToLibrary, setPublishToLibrary,
    description, setDescription,
    whenToUse, setWhenToUse,
    assetClasses, setAssetClasses,
    isNewsquawkOwned,

    // Toggles
    togglePortfolio,
    toggleCategory,
    toggleTagWatchlist,
    toggleIncluded,
    startFrom,
    clearAll,
    reset,

    // Derived
    isEditing,
    /** Which record is being edited: a watchlist, tag watchlist or combined one. */
    editingKind,
    /** Saved in place, never converted — see inPlaceReason above. */
    inPlace,
    inPlaceReason,
    /** What the Newsquawk list being copied is, for the look override. */
    libraryKind,
    /** The Newsquawk list being copied, if this is one. */
    basedOn: isEditing ? undefined : options.draft?.basedOn,
    /** Opened with a list the concierge assembled, rather than a copy. */
    fromDraft: !isEditing && !!options.draft && !options.draft.basedOn,
    copyUnchanged,
    /** The look can be kept for this user — see libraryChanges. */
    libraryLookable: libraryId != null,
    /** Terms, lists, name or alerts differ, so saving makes a new list. */
    libraryContentChanged: !!libraryChanges?.content,
    lookOnly,
    hasLibraryOverride,
    resetLibraryLook,
    selectedPortfolios,
    selectedGrouped,
    selectedTagWatchlistObjects,
    includedLists,
    blockedIncluded,
    /** The switches: which show, which are held at "or", the frame. */
    shape,
    filled,
    toCheck,
    keywordsSize,
    formErrors,

    // Data
    portfolios,
    knownPortfolios,
    combinedLists,
    tagsWatchlists,
    tickerIndex,
    isKnownTicker: isKnown,

    // Mutations
    handleSubmit,
    handleDelete,
    isSubmitting: isSaving || createMutation.isPending || editMutation.isPending,
    isDeleting,

    // Keyword limits
    MAX_KEYWORDS_COUNT,
    MAX_KEYWORDS_TOTAL_CHARS,
  };
}

export type UseFilterBuilderReturn = ReturnType<typeof useFilterBuilder>;
